import React, { useState, useRef, useEffect } from 'react'
import { ArrowUp, Paperclip, File, X, Square } from 'lucide-react'
import { TextArea } from '@/components/ui_custom/text-area'
import { Button } from '@/components/ui_custom/button'
import { ImagePreview } from '@/components/dialogs/image-preview'
import { useToast } from '@/hooks/use-toast'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSendMessage: (message: string, files?: File[]) => void
  onAddedFiles: (hasAddedFiles: boolean) => void
  onStopProcessing: () => void
  disabled?: boolean
  isLoadingMessage?: boolean
  noMessages?: boolean
  isStoppingProcessingLoading?: boolean
  conversationRef?: string
  streamingMsgRef?: string | null
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, onAddedFiles, onStopProcessing, disabled = false, isLoadingMessage = false, noMessages, isStoppingProcessingLoading = false, conversationRef, streamingMsgRef }) => {
  const [message, setMessage] = useState('')
  const { toast } = useToast()
  const draftKey = `chat-draft:${conversationRef ?? 'new'}`
  const [isDragging, setIsDragging] = useState(false)
  const [previews, setPreviews] = useState<{ file: File; preview: string }[]>([])
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false)
  const [previewImage, setPreviewImage] = useState<string>('')
  const [isStopProcessingDisabled, setIsStopProcessingDisabled] = useState<boolean>(isStoppingProcessingLoading || false)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const maxFiles = 3

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (message.trim() && !disabled) {
      setIsStopProcessingDisabled(true)
      onSendMessage(
        message,
        previews.map(item => item.file)
      )
      localStorage.removeItem(draftKey)
      setMessage('')
      setPreviews([])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleFiles = (files: FileList) => {
    if (disabled) return

    const fileArray = Array.from(files)

    if (fileArray.length > maxFiles) {
      toast({ variant: 'destructive', title: 'Ops ...', description: `Você só pode enviar até ${maxFiles} arquivos.` })
      setPreviews([])
      return
    }

    const newPreviews = fileArray.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }))
    setPreviews(prev => [...prev, ...newPreviews])
  }

  const removeFile = (index: number) => {
    setPreviews(prev => {
      const updated = [...prev]
      updated.splice(index, 1)
      return updated
    })
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
      e.dataTransfer.clearData()
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!disabled) setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleStopProcessing = () => {
    setIsStopProcessingDisabled(true)
    onStopProcessing()
  }

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = 'auto'
      textAreaRef.current.style.height = `${Math.min(textAreaRef.current.scrollHeight, 200)}px`
    }
  }, [message])

  // Restore draft on mount / conversation change
  useEffect(() => {
    const draft = localStorage.getItem(draftKey)
    if (draft) setMessage(draft)
    else setMessage('')
  }, [conversationRef])

  // Persist draft as user types (skip when disabled)
  useEffect(() => {
    if (disabled) return
    if (message) localStorage.setItem(draftKey, message)
    else localStorage.removeItem(draftKey)
  }, [message, conversationRef, disabled])

  useEffect(() => {
    onAddedFiles(!!previews.length)
  }, [previews])

  useEffect(() => {
    if (isStopProcessingDisabled) {
      setTimeout(() => {
        setIsStopProcessingDisabled(false)
      }, 5000)
    }
  }, [isStopProcessingDisabled])

  useEffect(() => {
    if (noMessages) {
      setIsStopProcessingDisabled(true)
    }
  }, [noMessages])

  // When streaming starts (first chunk arrives), make the stop button available
  useEffect(() => {
    if (streamingMsgRef) {
      setIsStopProcessingDisabled(false)
    }
  }, [streamingMsgRef])

  return (
    <div className={`w-full text-center px-2 rounded-lg transition-colors ${isDragging ? 'border-2 border-dashed border-blue-400 bg-blue-50' : ''}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      <input
        type="file"
        multiple
        hidden
        ref={fileInputRef}
        accept="image/*, application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.ms-powerpoint, application/vnd.ms-word, application/vnd.ms-excel, application/vnd.ms-powerpoint, application/vnd.ms-word, audio/*, video/*"
        onChange={e => e.target.files && handleFiles(e.target.files)}
      />

      <form className="flex items-end gap-2">
        <div className="flex-grow flex-row">
          {previews.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {previews.map(({ file, preview }, idx) => (
                <div
                  key={idx}
                  className="group/preview relative p-1.5 border border-border rounded-lg bg-primary/5 hover:bg-primary/10 cursor-pointer transition-colors duration-150 ease-out"
                  onClick={() => {
                    setPreviewImage(preview)
                    setIsPreviewOpen(true)
                  }}
                >
                  <div className="h-14 w-20 rounded-md overflow-hidden bg-background/50 flex items-center justify-center">
                    {file.type.startsWith('image/') ? (
                      <img src={preview} alt={file.name} className="h-full w-full object-contain" />
                    ) : (
                      <File className="h-8 w-8 text-gray-500" />
                    )}
                  </div>
                  <span className="block text-xs text-gray-600 truncate w-20 mt-1">{file.name}</span>
                  <Button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 text-xs opacity-0 group-hover/preview:opacity-100 transition-opacity duration-150 ease-out"
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <TextArea
            ref={textAreaRef}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Digite sua mensagem..."
            className="min-h-[40px] max-h-[200px] w-full resize-none border-none bg-transparent focus:outline-none focus:ring-0 shadow-none placeholder:text-muted-foreground text-foreground text-base transition-[height] duration-150 ease-out overflow-y-auto"
            disabled={disabled || isLoadingMessage}
          />
          {/* Linha de ações abaixo do textarea. flex-row + gap, alinhada à direita.
              Antes era `flex-col` (botões empilhavam após o fix do typo flex-column). */}
          <div className="flex flex-row items-center gap-2 justify-end pt-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || isLoadingMessage}
                  className="border border-border bg-background hover:bg-accent rounded-full h-10 w-10 p-0 text-foreground/70 hover:text-foreground transition-colors duration-150 ease-out disabled:opacity-50"
                  size="sm"
                  variant="outline"
                  aria-label="Anexar arquivo"
                >
                  <Paperclip className="h-[18px] w-[18px]" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-primary text-primary-foreground text-xs">
                Adicionar arquivo
              </TooltipContent>
            </Tooltip>

            {(isLoadingMessage || !!streamingMsgRef) ? (
              isStopProcessingDisabled ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      className="border-none rounded-full h-10 w-10 p-0 shadow-sm"
                      size="sm"
                      variant="primary"
                      aria-label="Processando"
                      disabled
                    >
                      <Square className="h-[18px] w-[18px] text-primary-foreground rounded-sm animate-spin" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-primary text-primary-foreground text-xs">
                    Processando...
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      className="border-none rounded-full h-10 w-10 p-0 shadow-sm transition-transform duration-150 ease-out active:scale-95"
                      size="sm"
                      variant="primary"
                      aria-label="Parar processamento"
                      onClick={handleStopProcessing}
                    >
                      <Square className="h-[18px] w-[18px] text-primary-foreground rounded-sm" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-primary text-primary-foreground text-xs">
                    Parar processamento
                  </TooltipContent>
                </Tooltip>
              )
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!message.trim() || disabled}
                    className={cn(
                      'border-none rounded-full h-10 w-10 p-0 transition-all duration-200 ease-out active:scale-95',
                      message.trim() ? 'shadow-sm' : ''
                    )}
                    size="sm"
                    variant={message.trim() ? 'primary' : 'outline'}
                    aria-label="Enviar mensagem"
                  >
                    <ArrowUp className="h-[18px] w-[18px]" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-primary text-primary-foreground text-xs">
                  Enviar
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </form>

      {previews?.length > 0 && <ImagePreview isOpen={isPreviewOpen} url={previewImage} onClose={() => setIsPreviewOpen(false)} />}
    </div>
  )
}
